/* ============================================================
   ASSETS/THEME.JS — Tu Sheló NABEL
   Global JS utilities: cart, toast, drawer, mobile nav
   ============================================================ */

/* ============================================================
   CART API
   ============================================================ */
const Cart = {
  async add(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
    if (!res.ok) throw new Error('Error al agregar al carrito');
    return res.json();
  },

  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async update(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    return res.json();
  },

  async change(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return res.json();
  },
};

/* ============================================================
   TOAST
   ============================================================ */
const Toast = {
  _el: null,
  _timer: null,

  _ensure() {
    if (this._el) return;
    this._el = document.createElement('div');
    this._el.className = 'toast';
    this._el.setAttribute('role', 'status');
    this._el.setAttribute('aria-live', 'polite');
    document.body.appendChild(this._el);
  },

  show(message, duration = 3000) {
    this._ensure();
    clearTimeout(this._timer);
    this._el.textContent = message;
    this._el.classList.add('toast--visible');
    this._timer = setTimeout(() => {
      this._el.classList.remove('toast--visible');
    }, duration);
  },
};

/* ============================================================
   SCROLL LOCK — shared by every overlay (drawers, search, modals)
   Setting overflow:hidden removes the scrollbar, which shrinks the
   viewport and shifts fixed/full-width content by the scrollbar's
   width. Compensate with matching padding-right so nothing moves.
   A counter supports overlapping locks (e.g. opening search while
   a drawer is already open) without unlocking too early.
   ============================================================ */
const ScrollLock = {
  _count: 0,

  lock() {
    if (this._count === 0) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) document.body.style.paddingRight = scrollbarWidth + 'px';
    }
    this._count++;
  },

  unlock() {
    this._count = Math.max(0, this._count - 1);
    if (this._count === 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  },
};

/* ============================================================
   OVERLAYS — only one menu/drawer/panel open at a time
   Each overlay registers itself when it opens, passing a function
   that closes it; opening another closes the rest first so they
   never stack or fight over scroll lock and focus.
   ============================================================ */
const Overlays = {
  _open: new Map(),

  opened(name, close) {
    [...this._open].forEach(([other, closeOther]) => {
      if (other !== name) closeOther();
    });
    this._open.set(name, close);
  },

  closed(name) {
    this._open.delete(name);
  },

  closeAll() {
    [...this._open.values()].forEach((close) => close());
  },
};

/* ============================================================
   DRAWER (cart drawer + mobile menu)
   ============================================================ */
const Drawer = {
  // Transition duration in ms — must match the CSS transition value
  _duration: 450,

  open(drawerId) {
    const el = document.getElementById(drawerId);
    // `scroll-lock` marks a drawer as open from the first frame (before
    // `is-open` is added), so double opens/closes never unbalance the lock.
    if (!el || el.hasAttribute('scroll-lock')) return;
    Overlays.opened(drawerId, () => this.close(drawerId));
    // Step 1: make element visible (display:block) in its OFF-SCREEN state
    el.setAttribute('open', '');
    el.setAttribute('scroll-lock', '');
    ScrollLock.lock();
    // Step 2: two rAF frames — first renders display:block, second fires the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add('is-open');
        el.querySelector('[data-drawer-close]')?.focus();
      });
    });
  },

  close(drawerId) {
    const el = document.getElementById(drawerId);
    if (!el || !el.hasAttribute('scroll-lock')) return;
    Overlays.closed(drawerId);
    // Step 1: remove is-open → CSS exit transition plays
    el.classList.remove('is-open');
    el.removeAttribute('scroll-lock');
    ScrollLock.unlock();
    // Step 2: after exit transition, hide the element completely
    setTimeout(() => {
      if (!el.classList.contains('is-open')) el.removeAttribute('open');
    }, this._duration);
  },

  toggle(drawerId) {
    const el = document.getElementById(drawerId);
    if (!el) return;
    el.hasAttribute('open') ? this.close(drawerId) : this.open(drawerId);
  },
};

/* ============================================================
   CART DRAWER — refresh & count badge
   ============================================================ */
const CartDrawer = {
  // `pulse`: play the added-to-cart cue the instant the count changes,
  // not after the (slower) drawer HTML fetch below.
  async refresh({ pulse = false } = {}) {
    const cart = await Cart.get();
    this._updateCount(cart.item_count);
    if (pulse) CartPulse.play();
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    const savedScroll = drawer.querySelector('.cart-drawer__items')?.scrollTop ?? 0;
    const oldFill = drawer.querySelector('.free-ship-progress__fill');
    const oldWidth = oldFill ? getComputedStyle(oldFill).width : null;
    try {
      // Fetch the section on the CURRENT page's own URL (not a bare '/'),
      // so the locale/market prefix (e.g. /en-us) is preserved — a bare
      // '/' always resolves to the store's default locale, which made the
      // drawer render in Spanish even while browsing in English.
      const refreshUrl = new URL(window.location.href);
      refreshUrl.searchParams.set('section_id', 'cart-drawer');
      const res = await fetch(refreshUrl.pathname + refreshUrl.search);
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.getElementById('cart-drawer');
      if (next) {
        drawer.innerHTML = next.innerHTML;
        const itemsEl = drawer.querySelector('.cart-drawer__items');
        if (itemsEl && savedScroll) itemsEl.scrollTop = savedScroll;

        // Snap the new fill bar back to its previous width (no transition),
        // then animate it forward to the new width on the next frame —
        // the innerHTML swap otherwise makes it appear already at rest.
        const newFill = drawer.querySelector('.free-ship-progress__fill');
        if (newFill && oldWidth !== null) {
          const targetWidth = newFill.style.width;
          newFill.style.transition = 'none';
          newFill.style.width = oldWidth;
          newFill.offsetHeight; // force reflow
          newFill.style.transition = '';
          requestAnimationFrame(() => {
            newFill.style.width = targetWidth;
          });
        }

        document.dispatchEvent(new CustomEvent('cart-drawer:refreshed'));
      }
    } catch (_) {}
  },

  _updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  },
};

/* ============================================================
   MARKET CART — clear/restore cart when the active market changes
   Carts aren't scoped per market by Shopify, so switching from
   México to Estados Unidos (or back) can leave line items in the
   cart that reference variants not sold in the new market. A
   snapshot of the outgoing market's cart is saved right before the
   market form submits; on the next page load, if the active market
   differs from the last one seen, the cart is cleared and — if a
   snapshot exists for the market being entered — restored on a
   best-effort basis (lines for variants unavailable there are
   silently skipped).
   ============================================================ */
const MarketCart = {
  _snapshotKey(marketHandle) {
    return 'shn_cart_snapshot_' + marketHandle;
  },

  async snapshotBeforeLeaving(fromMarketHandle) {
    if (!fromMarketHandle) return;
    try {
      const cart = await Cart.get();
      if (cart.item_count > 0) {
        const lines = cart.items.map((item) => ({ id: item.variant_id, quantity: item.quantity }));
        localStorage.setItem(this._snapshotKey(fromMarketHandle), JSON.stringify(lines));
      } else {
        localStorage.removeItem(this._snapshotKey(fromMarketHandle));
      }
    } catch (_) {}
  },

  async syncOnLoad(currentMarketHandle) {
    if (!currentMarketHandle) return;
    const lastMarket = localStorage.getItem('shn_last_market');
    localStorage.setItem('shn_last_market', currentMarketHandle);
    if (!lastMarket || lastMarket === currentMarketHandle) return;

    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    } catch (_) {}

    const snapshotRaw = localStorage.getItem(this._snapshotKey(currentMarketHandle));
    if (snapshotRaw) {
      try {
        const lines = JSON.parse(snapshotRaw);
        for (const line of lines) {
          try { await Cart.add(line.id, line.quantity); } catch (_) {}
        }
      } catch (_) {}
    }

    await CartDrawer.refresh();
  },
};

/* ============================================================
   CART PULSE — added-to-cart cue on the header cart icon
   Reveals the header if it was hidden by scrolling, then plays the
   `is-cart-added` animation (styles live in sections/header.liquid).
   ============================================================ */
const CartPulse = {
  play() {
    document.dispatchEvent(new CustomEvent('header:reveal'));
    document.querySelectorAll('.site-nav__icon-btn--cart').forEach((btn) => {
      btn.classList.remove('is-cart-added');
      btn.offsetWidth; // restart the animation on quick repeat adds
      btn.classList.add('is-cart-added');
      clearTimeout(btn._cartPulseTimer);
      btn._cartPulseTimer = setTimeout(() => btn.classList.remove('is-cart-added'), 1400);
    });
  },
};

/* ============================================================
   ADD TO CART (delegated handler)
   ============================================================ */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-product-form]');
  if (!form) return;
  e.preventDefault();

  const btn = form.querySelector('[data-add-to-cart]');
  if (btn) {
    btn.disabled = true;
    btn.dataset.originalText = btn.textContent;
    btn.innerHTML = '<span class="spinner"></span>';
  }

  try {
    const variantId = form.querySelector('[name="id"]')?.value;
    const quantity  = form.querySelector('[name="quantity"]')?.value ?? 1;
    await Cart.add(Number(variantId), Number(quantity));
    await CartDrawer.refresh({ pulse: true });
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<svg class="atc-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> ' + window.ShN_i18n.added + '!';
      btn.classList.add('btn--atc-success');
      setTimeout(() => {
        btn.classList.remove('btn--atc-success');
        btn.textContent = btn.dataset.originalText ?? window.ShN_i18n.addToCart;
      }, 2200);
    }
  } catch {
    Toast.show(window.ShN_i18n.addToCartError);
    if (btn) {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalText ?? window.ShN_i18n.addToCart;
    }
  }
});

/* ============================================================
   GLOBAL CLICK DELEGATION
   ============================================================ */
document.addEventListener('click', (e) => {
  // Drawer open
  const openTrigger = e.target.closest('[data-drawer-open]');
  if (openTrigger) {
    // A trigger that stays visible while its drawer is open (the hamburger)
    // doubles as the close button.
    const id = openTrigger.dataset.drawerOpen;
    document.getElementById(id)?.hasAttribute('scroll-lock') ? Drawer.close(id) : Drawer.open(id);
    return;
  }

  // Drawer close
  const closeTrigger = e.target.closest('[data-drawer-close]');
  if (closeTrigger) {
    const drawer = closeTrigger.closest('details[scroll-lock], [data-drawer]');
    if (drawer) Drawer.close(drawer.id);
    return;
  }

  // Backdrop click closes drawer
  const backdrop = e.target.closest('[data-drawer-backdrop]');
  if (backdrop) {
    const drawerId = backdrop.dataset.drawerBackdrop;
    if (drawerId) Drawer.close(drawerId);
  }
});

/* ============================================================
   ESCAPE KEY — closes any open drawer
   ============================================================ */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('details[scroll-lock][open]').forEach((el) => {
    Drawer.close(el.id);
  });
});

/* ============================================================
   QUANTITY SELECTOR
   ============================================================ */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-qty-btn]');
  if (!btn) return;
  const input = btn.closest('[data-qty-wrapper]')?.querySelector('input');
  if (!input) return;
  const step    = Number(input.step) || 1;
  const min     = Number(input.min)  || 1;
  const max     = input.max ? Number(input.max) : Infinity;
  const current = Number(input.value) || 1;
  const delta   = btn.dataset.qtyBtn === 'plus' ? step : -step;
  input.value   = Math.min(max, Math.max(min, current + delta));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ============================================================
   QUANTITY INPUT VALIDATION (all qty inputs site-wide)
   ============================================================ */
document.addEventListener('keydown', (e) => {
  if (!e.target.matches('.cart-drawer__qty-input, .qty-sel__input')) return;
  const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (allowed.includes(e.key) || /^[0-9]$/.test(e.key)) return;
  e.preventDefault(); // block -, +, ., e, E, etc.
});

document.addEventListener('input', (e) => {
  if (!e.target.matches('.cart-drawer__qty-input, .qty-sel__input')) return;
  const input = e.target;
  const cleaned = input.value.replace(/\D/g, '');
  if (input.value !== cleaned) input.value = cleaned;
  if (cleaned !== '' && parseInt(cleaned, 10) < 1) input.value = '1';
});

/* ============================================================
   POLÍTICAS (/policies/*)
   El contenido de las políticas viene del admin con subtítulos como
   <p><strong>…</strong></p>. Aquí se marcan como subtítulos, se oculta
   el título en mayúsculas que repite el h1 y la línea "Última
   actualización" se muestra en la pastilla del hero (snippets/policy-hero).
   ============================================================ */
(() => {
  const rte = document.querySelector('.policy-page .shopify-policy__body .rte');
  if (!rte) return;

  const pill = document.querySelector('[data-policy-pill]');
  const pillText = document.querySelector('[data-policy-pill-text]');
  const plain = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  let seenHeading = false;

  rte.querySelectorAll(':scope > p').forEach((p) => {
    const strong = p.querySelector(':scope > strong, :scope > b');
    const text = p.textContent.trim();
    if (!strong || p.children.length !== 1 || !text || text !== strong.textContent.trim()) return;

    if (/^(ultima actualizacion|last updated)/.test(plain(text))) {
      if (pill && pillText) {
        pillText.textContent = text;
        pill.classList.add('is-dated');
        p.hidden = true;
      }
      return;
    }
    if (!seenHeading && pill && text === text.toUpperCase()) {
      p.hidden = true;
      seenHeading = true;
      return;
    }
    seenHeading = true;
    p.classList.add('policy-heading');
    p.setAttribute('role', 'heading');
    p.setAttribute('aria-level', '2');
  });
})();

/* ============================================================
   AJAX FORM: envía formularios nativos de Shopify (contacto,
   newsletter) por fetch sin romper su hCaptcha invisible.
   El script del captcha guarda form.submit la primera vez que el
   usuario enfoca el formulario y lo llama ya con el token
   h-captcha-response. Reemplazar form.submit antes de eso hace que
   ese envío final vaya por fetch en lugar de recargar la página.
   ============================================================ */
const AjaxForm = {
  bind(form, { validate, onStart, onResult }) {
    let busy = false;

    const send = async () => {
      if (busy) return;
      busy = true;
      try {
        const res = await fetch(form.action, { method: 'POST', body: new FormData(form) });
        const url = new URL(res.url);
        // Shopify pidió un captcha visible: seguir el flujo nativo
        if (url.pathname.includes('/challenge')) {
          HTMLFormElement.prototype.submit.call(form);
          return;
        }
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        await onResult({ res, url, doc });
      } catch (error) {
        await onResult({ error });
      } finally {
        busy = false;
      }
    };

    form.submit = send;
    form.addEventListener('submit', (e) => {
      if (busy || (validate && !validate())) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      onStart?.();
      // Con captcha enlazado, su propio listener obtiene el token y llama a send()
      if (!form.dataset.hcaptchaBound) {
        e.preventDefault();
        e.stopImmediatePropagation();
        send();
      }
    });
  },
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/* ============================================================
   NEWSLETTER (footer): suscripción sin recargar la página
   Shopify responde igual para un correo nuevo que para uno ya
   registrado, así que "ya suscrito" solo se detecta con lo que
   este navegador recuerda (localStorage) o con la sesión del cliente
   (Liquid, en sections/footer.liquid).
   ============================================================ */
(() => {
  const form = document.getElementById('footer-newsletter');
  const input = form?.querySelector('input[type="email"]');
  if (!input) return;

  const row = form.querySelector('[data-nl-row]');
  const msg = form.querySelector('[data-nl-msg]');
  const i18n = window.ShN_i18n;
  const STORE_KEY = 'shn:newsletter';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  let startedAt = 0;

  const known = () => {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || []; } catch { return []; }
  };
  const remember = (email) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify([...new Set([...known(), email])].slice(-5))); } catch {}
  };
  const email = () => input.value.trim().toLowerCase();

  const setState = (state) => {
    form.classList.remove('is-loading', 'is-done', 'is-error');
    if (!state) return;
    void form.offsetWidth; // reinicia la animación si se repite el mismo estado
    form.classList.add(state);
  };
  const say = (text = '', info = false) => {
    msg.textContent = text;
    msg.classList.toggle('is-info', info);
    msg.hidden = !text;
  };

  // Sustituye el campo por el mensaje final, con salida/entrada animadas
  const finish = (text, info = false) => {
    const p = document.createElement('p');
    p.className = 'site-footer__nl-success is-entering' + (info ? ' site-footer__nl-success--info' : '');
    p.setAttribute('role', 'status');
    const icon = form.querySelector('.site-footer__nl-btn-done svg').cloneNode(true);
    icon.classList.add('home-icon');
    const label = document.createElement('span');
    label.textContent = text;
    p.append(icon, label);
    row.classList.add('is-leaving');
    setTimeout(() => form.replaceChildren(p), 250);
  };

  input.addEventListener('input', () => {
    if (form.classList.contains('is-error')) { setState(null); say(); }
  });

  AjaxForm.bind(form, {
    validate() {
      if (form.classList.contains('is-done')) return false;
      if (!EMAIL_RE.test(email())) {
        setState('is-error');
        say(i18n.newsletterInvalid);
        input.focus();
        return false;
      }
      if (known().includes(email())) {
        setState(null);
        say();
        finish(i18n.newsletterAlready, true);
        return false;
      }
      return true;
    },
    onStart() {
      startedAt = Date.now();
      say();
      setState('is-loading');
      input.readOnly = true;
    },
    async onResult({ res, url, doc, error }) {
      // Tiempo mínimo para que la animación se alcance a ver
      await wait(700 - (Date.now() - startedAt));
      input.readOnly = false;
      const posted = !error && res.ok && (url.searchParams.get('customer_posted') === 'true'
        || doc.querySelector('#footer-newsletter .site-footer__nl-success'));
      if (!posted) {
        setState('is-error');
        say(doc?.querySelector('#footer-newsletter [data-nl-msg]')?.textContent.trim() || i18n.newsletterError);
        return;
      }
      remember(email());
      setState('is-done');
      await wait(900);
      finish(i18n.newsletterSuccess);
    },
  });
})();

/* ============================================================
   CONTACTO (/pages/contact): envío sin recargar la página
   ============================================================ */
(() => {
  const form = document.getElementById('contact-page-form');
  if (!form || !form.querySelector('[type="submit"]')) return;

  const wrap = form.closest('.pcon-form-wrap');
  let startedAt = 0;

  const setState = (state) => {
    form.classList.remove('is-loading', 'is-done', 'is-error');
    if (!state) return;
    void form.offsetWidth;
    form.classList.add(state);
  };

  const showError = (html) => {
    let box = form.querySelector('.pcon-error');
    if (!box) {
      box = document.createElement('div');
      box.className = 'pcon-error';
      box.setAttribute('role', 'alert');
      form.prepend(box);
    }
    box.innerHTML = html;
    box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  // Cambia el formulario por la confirmación animando la altura de la tarjeta
  const showSuccess = (successEl) => {
    const from = wrap.offsetHeight;
    form.classList.add('is-leaving');
    setTimeout(() => {
      successEl.classList.add('is-entering');
      form.replaceChildren(successEl);
      form.classList.remove('is-leaving', 'is-done');
      const to = wrap.offsetHeight;
      wrap.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      successEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 250);
  };

  AjaxForm.bind(form, {
    onStart() {
      startedAt = Date.now();
      form.querySelector('.pcon-error')?.remove();
      setState('is-loading');
      form.setAttribute('aria-busy', 'true');
    },
    async onResult({ res, doc, error }) {
      await wait(800 - (Date.now() - startedAt));
      form.removeAttribute('aria-busy');
      const success = !error && res.ok && doc.querySelector('#contact-page-form .pcon-success');
      if (!success) {
        setState('is-error');
        const serverError = doc?.querySelector('#contact-page-form .pcon-error');
        showError(serverError ? serverError.innerHTML : window.ShN_i18n.genericError);
        return;
      }
      setState('is-done');
      await wait(900);
      showSuccess(document.importNode(success, true));
    },
  });
})();

/* ============================================================
   EXPOSE GLOBALS
   ============================================================ */
window.ShN = { Cart, Toast, Overlays, Drawer, CartDrawer, MarketCart, ScrollLock, AjaxForm, CartPulse };

MarketCart.syncOnLoad(document.body.dataset.market);
